import React from "react"
import { useNavigate } from "@tanstack/react-router"
import {
  Modal,
  Spin,
  Space,
  Tag,
  Typography,
  Alert,
  Descriptions,
  Button,
  CalendarOutlined,
} from "@mimber/ui-web"
import { useEvent } from "@mimber/client/mimber-organization"
import {
  EventStatus,
  EVENT_STATUS_DISPLAY,
  EVENT_LOCATION_TYPE_DISPLAY,
} from "@mimber/schema"
import { EditEventCalendarForm } from "@/components/forms/EditEventCalendarForm"
import type { EditEventCalendarModalProps } from "./EditEventCalendarModal.types"

const { Title } = Typography

export const EditEventCalendarModal: React.FunctionComponent<
  EditEventCalendarModalProps
> = ({ eventUuid, onSuccess, ...modalProps }) => {
  const { data, isLoading } = useEvent(eventUuid)
  const navigate = useNavigate()
  const event = data?.data
  const isCancelled = event?.status === EventStatus.CANCELLED

  return (
    <Modal
      title="Reschedule Event"
      footer={null}
      destroyOnHidden
      {...modalProps}
    >
      {(close) =>
        isLoading || !event ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
            <Space
              style={{ width: "100%", justifyContent: "space-between" }}
              align="start"
            >
              <Space orientation="vertical" size={4}>
                <Title level={4} style={{ margin: 0 }}>
                  {event.title}
                </Title>
                <Tag
                  color={
                    EVENT_STATUS_DISPLAY[event.status]?.color ?? "default"
                  }
                >
                  {EVENT_STATUS_DISPLAY[event.status]?.label ?? event.status}
                </Tag>
              </Space>
              <Button
                icon={<CalendarOutlined />}
                onClick={() => {
                  navigate({
                    to: "/events/$eventUuid/$tabKey",
                    params: { eventUuid: event.uuid, tabKey: "overview" },
                  })
                  close()
                }}
              >
                View Event
              </Button>
            </Space>

            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Start Date">
                {new Date(event.startDate).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="End Date">
                {new Date(event.endDate).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Timezone">
                {event.timezone}
              </Descriptions.Item>
              <Descriptions.Item label="Location">
                {EVENT_LOCATION_TYPE_DISPLAY[event.locationType]?.label ??
                  event.locationType}
                {event.physicalAddress && ` — ${event.physicalAddress}`}
              </Descriptions.Item>
            </Descriptions>

            {isCancelled ? (
              <Alert
                type="warning"
                showIcon
                message="This event has been cancelled"
                description="Cancelled events cannot be rescheduled. View the event for more details."
              />
            ) : (
              <EditEventCalendarForm
                event={event}
                onSuccess={() => {
                  onSuccess?.()
                  close()
                }}
                onCancel={close}
              />
            )}
          </Space>
        )
      }
    </Modal>
  )
}
